<template>
  <div v-if="customerEmail" class="bg-white rounded-2xl shadow-md p-4">
    <div class="flex items-center justify-between mb-3">
      <h3 class="text-lg font-bold text-gray-900">{{ t("title") }}</h3>
      <a
        v-if="intercomContactUrl"
        :href="intercomContactUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1 text-xs text-primary hover:underline"
      >
        {{ t("view_in_intercom") }}
        <ExternalLinkIcon class="h-3.5 w-3.5" />
      </a>
    </div>

    <div v-if="loading" class="flex justify-center py-4">
      <Spinner />
    </div>

    <template v-else>
      <p v-if="error" class="text-sm text-red-500">
        {{ t("error") }}
      </p>

      <p v-else-if="conversations.length === 0" class="text-sm text-gray-500">
        {{ t("empty_state") }}
      </p>

      <div v-else class="space-y-2">
        <IntercomConversationCard
          v-for="conversation in conversations"
          :key="conversation.id"
          :conversation="conversation"
          @click="openConversation(conversation.id)"
        />

        <button
          v-if="hasMore"
          type="button"
          class="w-full text-sm text-primary hover:underline py-2"
          :disabled="loadingMore"
          @click="loadMore"
        >
          <Spinner v-if="loadingMore" class="inline" />
          <span v-else>{{ t("load_more") }}</span>
        </button>
      </div>
    </template>

    <IntercomConversationModal
      :open="!!selectedConversationId"
      :conversation-id="selectedConversationId"
      @close="selectedConversationId = null"
    />
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { Spinner, toCamelCase } from "@estaly/ui";
import { ExternalLinkIcon } from "@heroicons/vue/outline";
import IntercomService from "@/api/services/intercom";
import IntercomConversationCard from "./IntercomConversationCard.vue";
import IntercomConversationModal from "./IntercomConversationModal.vue";

const PER_PAGE = 3;

export default {
  name: "IntercomConversationList",

  components: { Spinner, ExternalLinkIcon, IntercomConversationCard, IntercomConversationModal },

  props: {
    customerEmail: {
      type: String,
      default: null,
    },
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      conversations: [],
      loading: false,
      loadingMore: false,
      cursor: null,
      hasMore: false,
      selectedConversationId: null,
      intercomContactUrl: null,
      error: false,
    };
  },

  watch: {
    customerEmail(newEmail) {
      this.resetState();
      if (newEmail) this.fetchConversations();
    },
  },

  mounted() {
    if (this.customerEmail) this.fetchConversations();
  },

  methods: {
    resetState() {
      this.conversations = [];
      this.cursor = null;
      this.hasMore = false;
      this.selectedConversationId = null;
      this.intercomContactUrl = null;
      this.error = false;
    },

    async fetchConversations() {
      this.loading = true;
      this.error = false;
      const email = this.customerEmail;

      const response = await IntercomService.fetchConversations({
        email,
        perPage: PER_PAGE,
      });

      if (this.customerEmail !== email) return;

      if (response.success === false) {
        this.error = true;
      } else {
        const data = toCamelCase(response.data);
        this.conversations = data.conversations || [];
        this.cursor = data.pages?.startingAfter || null;
        this.hasMore = !!this.cursor;
        this.intercomContactUrl = data.intercomContactUrl || null;
      }

      this.loading = false;
    },

    async loadMore() {
      if (!this.cursor) return;
      this.loadingMore = true;
      const email = this.customerEmail;

      const response = await IntercomService.fetchConversations({
        email,
        perPage: PER_PAGE,
        startingAfter: this.cursor,
      });

      if (this.customerEmail !== email) return;

      if (response.success !== false) {
        const data = toCamelCase(response.data);
        this.conversations.push(...(data.conversations || []));
        this.cursor = data.pages?.startingAfter || null;
        this.hasMore = !!this.cursor;
      }

      this.loadingMore = false;
    },

    openConversation(id) {
      this.selectedConversationId = id;
    },
  },
};
</script>

<i18n src="./IntercomConversationList.i18n.json"></i18n>
